import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import styled from 'styled-components';

import {
  Rarity,
  So5LeaderboardSeasonality,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import {
  Dropdown,
  DropdownOptionLabel,
} from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { ChevronDownBold } from '@sorare/core/src/atoms/icons/ChevronDownBold';
import { SeasonalityIcon } from '@sorare/core/src/atoms/icons/SeasonalityIcon';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { glossary } from '@sorare/core/src/lib/glossary';
import { StatsRarity } from '@sorare/core/src/lib/playerSearchView';

import { SelectRarity } from 'atoms/SelectRarity';

import { Block } from '../ui';

const StyledDropdownOptionLabel = styled(DropdownOptionLabel)`
  align-items: center;
  svg,
  img {
    margin-right: var(--unit);
  }
`;

const StyledCenterDropdownOptionLabel = styled(StyledDropdownOptionLabel)`
  justify-content: center;
`;

const StyledChevronDownBold = styled(ChevronDownBold)`
  transition: transform 0.3s;
  &.chevron-up {
    transform: rotate(180deg);
  }
`;

const seasonalities = [
  So5LeaderboardSeasonality.IN_SEASON,
  So5LeaderboardSeasonality.ALL_SEASONS,
];
const StyledBlock = styled(Block)`
  background-color: var(--c-black);
  position: sticky;
  top: 0;
  z-index: 1;
`;

const rarities: StatsRarity[] = [
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
  Rarity.unique,
];

type Props = {
  filters: {
    seasonality: So5LeaderboardSeasonality;
    rarity: StatsRarity;
  };
  setRarity: (r: StatsRarity) => void;
  setSeasonality: (s: So5LeaderboardSeasonality) => void;
  onClose: () => void;
};
export const ScoutingCenterHeader = ({
  filters: { rarity, seasonality },
  setRarity,
  setSeasonality,
  onClose,
}: Props) => {
  const { formatMessage } = useIntlContext();
  const {
    so5: { inSeasonName, allSeasonsName },
  } = useConfigContext();

  return (
    <StyledBlock>
      <Horizontal>
        <IconButton
          onClick={onClose}
          icon={faChevronLeft}
          color="transparent"
          aria-label={formatMessage(glossary.close)}
        />
        <Dropdown
          align="right"
          label={({ open, ...props }) => (
            <Button
              size="small"
              fullWidth
              startIcon={<SeasonalityIcon seasonality={seasonality} />}
              color="quaternary"
              endIcon={
                <StyledChevronDownBold className={open ? 'chevron-up' : ''} />
              }
              {...props}
            >
              {seasonality === So5LeaderboardSeasonality.IN_SEASON
                ? inSeasonName
                : allSeasonsName}
            </Button>
          )}
          closeOnChange
          fullWidth
        >
          {({ closeDropdown }) =>
            seasonalities.map(s => (
              <StyledCenterDropdownOptionLabel
                key={s}
                onClick={() => {
                  setSeasonality(s);
                  closeDropdown();
                }}
              >
                <SeasonalityIcon seasonality={s} />
                {s === So5LeaderboardSeasonality.IN_SEASON
                  ? inSeasonName
                  : allSeasonsName}
              </StyledCenterDropdownOptionLabel>
            ))
          }
        </Dropdown>
        <SelectRarity
          rarities={rarities}
          selectedRarity={rarity}
          onChange={setRarity}
        />
      </Horizontal>
    </StyledBlock>
  );
};
